import { Footer, Header } from '@hcrshubham/core-components';
import { persistor, store } from '@hcrshubham/store';
import AOS from 'aos';
import 'aos/dist/aos.css';
import 'bootstrap/dist/css/bootstrap.min.css';
import { AnimatePresence } from 'framer-motion';
import { ThemeProvider } from 'next-themes';
import { AppProps } from 'next/app';
import { Lato } from 'next/font/google';
import Head from 'next/head';
import { DefaultSeo } from 'next-seo';
import { useEffect } from 'react';
import { Provider } from 'react-redux';
import { PersistGate } from 'redux-persist/integration/react';

import {
  registerGTM,
  registerHotjar,
  registerVercelAnalytics,
} from '../utils/tracking.utils';

import './styles-light.scss';
import './styles.scss';

const siteUrl = (process.env.NEXT_PUBLIC_SITE_URL || 'https://shubhamsharma.in').replace(/\/$/, '');

const font = Lato({
  weight: ['100', '300', '400', '700', '900'],
  subsets: ['latin'],
  display: 'swap',
  preload: true,
});

const CustomApp = ({ Component, pageProps }: AppProps) => {
  useEffect(() => {
    AOS.init();
    const onLoad = () => {
      setTimeout(() => {
        registerHotjar();
      }, 1000);
    };

    if (document.readyState === 'complete') {
      onLoad();
      return;
    }

    window.addEventListener('load', onLoad);
    return () => window.removeEventListener('load', onLoad);
  }, []);

  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        <ThemeProvider
          defaultTheme="system"
          enableSystem
          enableColorScheme
          storageKey="portfolio-theme"
        >
          <Head>
            <link
              rel="stylesheet"
              href="/cdn/iconoir-subset.min.css"
            />
            <link rel="icon" href="/favicon.ico" />
            <link rel="apple-touch-icon" href="/apple-touch-icon.png" />
          </Head>

          <Head>
            <meta httpEquiv="Content-Language" content="en" />
            <script
              type="application/ld+json"
              dangerouslySetInnerHTML={{
                __html: JSON.stringify({
                  '@context': 'https://schema.org',
                  '@type': 'WebSite',
                  name: 'Shubham Kumar Sharma',
                  url: siteUrl,
                  inLanguage: 'en',
                }),
              }}
            />
          </Head>

          <DefaultSeo
            title="Shubham Kumar Sharma"
            description="I'm a Full Stack Developer specializing in Java, Spring Boot, SQL, and NoSQL, with a focus on building scalable and high-performance applications."
            openGraph={{
              type: 'website',
              url: `${siteUrl}/`,
              title: 'Shubham Kumar Sharma',
              description:
                'I am a Full Stack Developer with expertise in Java, Spring Boot, SQL, and NoSQL.',
              images: [
                {
                  url: `${siteUrl}/assets/dpPhoto.jpg`,
                  width: 800,
                  height: 600,
                  alt: 'Shubham Kumar Sharma',
                },
              ],
              site_name: 'Shubham Kumar Sharma',
            }}
            twitter={{
              handle: '@hcrshubham',
              site: '@hcrshubham',
              cardType: 'summary_large_image',
            }}
          />

          {registerGTM()}
          <AnimatePresence mode="wait" initial={false}>
            <main className={font.className}>
              <Header />
              <Component {...pageProps} />
              <Footer />
              {registerVercelAnalytics()}
            </main>
          </AnimatePresence>
        </ThemeProvider>
      </PersistGate>
    </Provider>
  );
};

export default CustomApp;

